import React, { useState } from "react";
import { defaultPfpUrl } from "../utils";
import { Badge } from "../ui/badge";
import ProfileLocationLink from "./ProfileLocationLink";
import ProfilePhotoModal from "./ProfilePhotoModal";

/**
 * Organization profile header. Edit is only shown when signed in as this org.
 * @param {Object} props
 * @returns {JSX.Element}
 */
const OrganizationProfileHeader = ({ organization, userId, targetUserId, isEditing, onEdit }) => {
    const canEdit = userId != null && targetUserId != null && String(userId) === String(targetUserId);
    const [photoOpen, setPhotoOpen] = useState(false);
    const orgName = organization.name || "Organization";

    return (
        <div className="border-b border-ui-border bg-primary shadow-sm">
            <div className="mx-auto max-w-4xl px-4 py-6 sm:py-8">
                <div className="flex min-w-0 items-center gap-4 sm:gap-6">
                    <div className="shrink-0">
                        <button
                            type="button"
                            onClick={() => setPhotoOpen(true)}
                            className="cursor-pointer rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ui-ring focus-visible:ring-offset-2 focus-visible:ring-offset-primary"
                            aria-label={`View ${orgName} logo`}
                        >
                            <img
                                src={organization.pfp_url || defaultPfpUrl}
                                alt=""
                                className="h-16 w-16 rounded-full border-2 border-border bg-secondary object-cover shadow-lg sm:h-24 sm:w-24"
                                onError={(e) => {
                                    e.target.src = defaultPfpUrl;
                                }}
                            />
                        </button>
                        <ProfilePhotoModal
                            open={photoOpen}
                            onOpenChange={setPhotoOpen}
                            src={organization.pfp_url || defaultPfpUrl}
                            alt={`${orgName} logo`}
                        />
                    </div>

                    <div className="min-w-0 flex-1">
                        <div className="flex min-w-0 flex-wrap items-center gap-2 sm:gap-3">
                            <h1 className="break-words text-2xl font-bold text-text-primary sm:text-3xl">
                                {organization.name || "Organization"}
                            </h1>
                            {organization.denomination ? (
                                <Badge variant="accent">{organization.denomination}</Badge>
                            ) : null}
                            {canEdit && !isEditing ? (
                                <button
                                    type="button"
                                    onClick={onEdit}
                                    className="rounded-md border border-accent bg-primary px-3 py-1 text-sm text-accent transition hover:bg-secondary"
                                >
                                    Edit
                                </button>
                            ) : null}
                        </div>
                        <ProfileLocationLink
                            label={organization.address}
                            latitude={organization.latitude}
                            longitude={organization.longitude}
                            className="mt-1 block break-words text-sm text-text-muted"
                        />
                        {organization.website ? (
                            <a
                                href={
                                    organization.website.startsWith("http")
                                        ? organization.website
                                        : `https://${organization.website}`
                                }
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-block max-w-full truncate text-sm text-accent underline hover:text-accent-hover"
                            >
                                {organization.website}
                            </a>
                        ) : null}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default OrganizationProfileHeader;
